import { useState } from 'react';
import { ItemCategory, ItemCommissionType, Items } from '@prisma/client';
import { Box, capitalize } from '@mui/material';
import { Form, Formik } from 'formik';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { FormikForm, InputRowType } from '../../interfaces';
import {
  itemForm,
  ItemForm as ItemFormType,
  ItemWithCommissions,
} from '../../types/items';
import useUser from '../../hooks/global/useUser';
import InputGroup from '../form/InputGroup';
import EditableInfoSection from '../form/EditableInfoSection';
import ImageInput from '../form/ImageInput';
import ItemCommissionsModal from './ItemCommissionsModal';
import { getCommissionEmployees } from '../../helpers/items';
import useCreateItem from '../../hooks/items/useCreateItem';
import useUpdateItem from '../../hooks/items/useUpdateItem';
import { invalidateSearchItems, resetSearchItems } from '../../services/items';
import { Optional } from '../../types/global';
import useGetOnboarding from '../../hooks/businesses/useGetOnboarding';
import { ImageItem } from '../../utils/form/appointmentForm';
import { roundToMaxDecimalPlaces } from '../../utils/global/helper';
import { PERCENT_MAX_TAX_DECIMAL_PLACES } from '../../types/businesses';

type UpdateType = 'create' | 'update';

interface Props {
  item?: Optional<ItemWithCommissions, 'id'> | null;
  onSuccess?: (item: Items, type: UpdateType) => void | Promise<void>;
}

function ItemForm({ item, onSuccess: onSuccessProp }: Props) {
  const { user } = useUser();
  const { reloadAfterUpdate } = useGetOnboarding();

  const [isEditCommissionModalOpen, setIsEditCommissionModalOpen] =
    useState(false);

  const onSuccess = async (successItem: Items, type: UpdateType) => {
    if (type === 'create') {
      await resetSearchItems();
    } else {
      await invalidateSearchItems();
    }
    reloadAfterUpdate('ITEM_LIBRARY');
    onSuccessProp?.(successItem, type);

    toast.success(`Item ${item?.id ? 'updated' : 'created'}!`);
  };

  const onError = (error: string) => {
    toast.error(error);
  };

  const { mutateAsync: createItem, isPending: isPendingCreate } = useCreateItem(
    {
      onSuccess: successItem => onSuccess(successItem, 'create'),
      onError,
    },
  );
  const { mutateAsync: updateItem, isPending: isPendingUpdate } = useUpdateItem(
    {
      onSuccess: successItem => onSuccess(successItem, 'update'),
      onError,
    },
  );

  const isPending = isPendingCreate || isPendingUpdate;

  const productTaxRate = user?.business?.businessTaxRates?.productTaxRate || 0;
  const productTaxPercent = roundToMaxDecimalPlaces(
    productTaxRate * 100,
    PERCENT_MAX_TAX_DECIMAL_PLACES,
  );

  const form: FormikForm<ItemFormType> = {
    onSubmit: async values => {
      try {
        if (item?.id) {
          await updateItem({ itemId: item.id, payload: values });
        } else {
          await createItem({ payload: values });
        }
      } catch (error) {
        // Do nothing
      }
    },
    initialValues: {
      name: item?.name || '',
      category: (item?.category || '') as ItemCategory,
      price: (item ? item.price || 0 : '') as number,
      isTaxable: item?.isTaxable ?? productTaxRate > 0,
      commissionType: item?.commissionType || 'REVENUE',
      employeeCommissions: item?.employeeCommissions || [],
      cost: (item ? item.cost || 0 : '') as number,
      images:
        item?.images?.map(i => ({
          id: String(i.id),
          url: i.url,
          isUploaded: !!item.id,
        })) ?? [],
    },
    validationSchema: itemForm,
  };

  const employeesAvailableForCommissions =
    user?.business?.type === 'STUDIO'
      ? getCommissionEmployees(user?.business?.employees)
      : [];

  return (
    <Box>
      <Formik
        enableReinitialize
        initialValues={form.initialValues}
        validationSchema={form.validationSchema}
        onSubmit={form.onSubmit}>
        {({
          isSubmitting,
          values,
          isValid,
          dirty: formikDirty,
          setFieldValue,
        }) => (
          <Form>
            <InputGroup
              name="Item Details"
              rows={[
                [
                  {
                    label: 'Name *',
                    name: 'name',
                    type: 'text',
                    placeholder: 'Enter item name',
                  },
                ],
                [
                  {
                    label: 'Category *',
                    name: 'category',
                    type: 'select',
                    placeholder: 'Select category',
                    options: Object.values(ItemCategory)
                      .filter(category => category !== 'GIFT_CARD')
                      .map(category => ({
                        label: capitalize(category.toLowerCase()),
                        value: category,
                      })),
                    autoWidth: false,
                  },
                ],
                [
                  {
                    label: 'Default Price *',
                    name: 'price',
                    type: 'dollar',
                    placeholder: 'Enter default price',
                  },
                ],
                [
                  {
                    label: 'Sales Tax *',
                    name: 'isTaxable',
                    type: 'select',
                    placeholder: 'Select sales tax',
                    options: [
                      {
                        label: 'Non-taxable item',
                        value: false,
                      },
                      {
                        label: `Collect product sales tax (${productTaxPercent}%)`,
                        value: true,
                      },
                    ],
                    autoWidth: false,
                  },
                ],
              ]}
            />

            <Box className="input-group">
              <ImageInput
                name="itemImage"
                label="Image"
                images={(values.images ?? []) as ImageItem[]}
                setImages={updater => {
                  const current = (values.images ?? []) as ImageItem[];
                  const next =
                    typeof updater === 'function' ? updater(current) : updater;
                  setFieldValue('images', next);
                }}
                maxImages={1}
              />
            </Box>

            {!!employeesAvailableForCommissions?.length && (
              <>
                <EditableInfoSection
                  title="Payout Details"
                  description="Optionally, add in commission rates for each user. Whenever this product is sold and attached to a service provider, we’ll split up the payment based on their designated commission rate . Only users that are paid on commissions can be set up with a product commission rate. "
                  rows={employeesAvailableForCommissions.map(employee => ({
                    title:
                      employee.displayName ||
                      employee.user?.firstName ||
                      'Unnamed',
                    value: `${
                      (values.employeeCommissions?.find(
                        ({ employeeId }) => employeeId === employee.employeeId,
                      )?.rate || 0) * 100
                    }%`,
                  }))}
                  button={{
                    text: 'Edit Commissions',
                    onClick: () => setIsEditCommissionModalOpen(true),
                  }}
                />
                {isEditCommissionModalOpen && (
                  <ItemCommissionsModal
                    open
                    onClose={() => setIsEditCommissionModalOpen(false)}
                  />
                )}
              </>
            )}

            <InputGroup
              name="Other Details"
              rows={
                [
                  [
                    {
                      label: 'Cost',
                      name: 'cost',
                      type: 'dollar',
                      placeholder: 'Enter cost',
                      tooltip: 'The cost to stock this item',
                    },
                  ],
                  !!values.employeeCommissions?.some(({ rate }) => rate > 0) &&
                  !!employeesAvailableForCommissions?.length
                    ? [
                        {
                          label: 'Commission Type *',
                          name: 'commissionType',
                          type: 'select',
                          placeholder: 'Select commission type',
                          options: [
                            {
                              label: 'Pay commissions on Revenue',
                              value: ItemCommissionType.REVENUE,
                            },
                            {
                              label: 'Pay commissions on Profit',
                              value: ItemCommissionType.PROFIT,
                            },
                          ],
                          autoWidth: false,
                        },
                      ]
                    : null,
                ].filter(Boolean) as InputRowType[]
              }
            />

            <LoadingButton
              type="submit"
              disabled={
                !isValid ||
                isSubmitting ||
                (!!item?.id && !formikDirty) ||
                isPending
              }
              loading={isPending || isSubmitting}
              variant="contained"
              className="primary">
              {item?.id ? 'Save' : 'Create'}
            </LoadingButton>
          </Form>
        )}
      </Formik>
    </Box>
  );
}

export default ItemForm;
